import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const FileViewerModal = ({
  isOpen,
  onClose,
  file,
  onDownload
}) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && file) {
      loadFilePreview();
    }

    // Cleanup object URL when modal closes
    return () => {
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [isOpen, file]);

  const loadFilePreview = async () => {
    if (!file) return;

    setIsLoading(true);
    setError(null);

    try {
      // Handle different file object structures
      let url = null;

      // For evidence files (from EvidenceTabContent)
      if (file.file_url) {
        url = file.file_url;
      }
      // For attachment files (from MinutesTabContent) - simple string URL
      else if (typeof file === 'string') {
        url = file;
      }
      // For other file objects with url property
      else if (file.url) {
        url = file.url;
      }

      // Check if it's a PDF and set loading state
      const fileType = getFileType(url || '');
      if (fileType === 'application/pdf') {
        setIsPdfLoading(true);
      }

      if (url && getFileType(url).startsWith('image/')) {
        setFileUrl(url);
      } else {
        setFileUrl(url);
      }
    } catch (err) {
      console.error('Error loading file preview:', err);
      setError('Failed to load file preview');
    } finally {
      setIsLoading(false);
    }
  };

  const getFileType = (url) => {
    if (!url) return '';

    // For blob URLs, check the filename from the file object
    if (url.startsWith('blob:') && file?.filename) {
      const extension = file.filename.toLowerCase().split('.').pop();
      return getFileTypeFromExtension(extension);
    }

    // Check file extension from URL
    const extension = url.toLowerCase().split('.').pop();
    return getFileTypeFromExtension(extension);
  };

  const getFileTypeFromExtension = (extension) => {

    switch (extension) {
      // Image types
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'svg':
        return 'image/svg+xml';
      case 'bmp':
        return 'image/bmp';
      case 'ico':
        return 'image/x-icon';

      // Document types
      case 'pdf':
        return 'application/pdf';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'ppt':
        return 'application/vnd.ms-powerpoint';
      case 'pptx':
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'odt':
        return 'application/vnd.oasis.opendocument.text';
      case 'ods':
        return 'application/vnd.oasis.opendocument.spreadsheet';
      case 'odp':
        return 'application/vnd.oasis.opendocument.presentation';

      // Text types
      case 'txt':
        return 'text/plain';
      case 'rtf':
        return 'application/rtf';
      case 'csv':
        return 'text/csv';

      // Archive types
      case 'zip':
        return 'application/zip';
      case 'rar':
        return 'application/vnd.rar';
      case '7z':
        return 'application/x-7z-compressed';
      case 'tar':
        return 'application/x-tar';
      case 'gz':
        return 'application/gzip';

      // Audio types
      case 'mp3':
        return 'audio/mpeg';
      case 'wav':
        return 'audio/wav';
      case 'ogg':
        return 'audio/ogg';
      case 'm4a':
        return 'audio/mp4';

      // Video types
      case 'mp4':
        return 'video/mp4';
      case 'avi':
        return 'video/x-msvideo';
      case 'mov':
        return 'video/quicktime';
      case 'wmv':
        return 'video/x-ms-wmv';
      case 'flv':
        return 'video/x-flv';
      case 'webm':
        return 'video/webm';
      case 'mkv':
        return 'video/x-matroska';

      default:
        return 'application/octet-stream';
    }
  };

  const getFileName = () => {
    // For evidence files
    if (file?.filename) return file.filename;
    if (file?.title) return file.title;

    // For attachment URLs
    if (typeof file === 'string') {
      return cleanFileName(file);
    }

    // For other file objects
    if (file?.name) return file.name;

    return 'Unknown File';
  };

  const getFileUrl = () => {
    if (file?.file_url) return file.file_url;
    if (typeof file === 'string') return file;
    if (file?.url) return file.url;
    return null;
  };

  const cleanFileName = (url) => {
    if (!url) return 'Unknown File';
    return url.split('/').pop() || 'Unknown File';
  };

  const handleDownload = async () => {
    const url = getFileUrl();
    if (onDownload && file?.id) {
      // For evidence files with custom download handler
      onDownload(file.id);
    } else if (url) {
      setIsDownloading(true);
      try {
        // Try to download the file
        const response = await fetch(url);
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = getFileName();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the blob URL
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Download failed, opening in new tab instead:', error);
        // Fallback: open in new tab if download fails
        window.open(url, '_blank');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  const handleOpenInNewTab = () => {
    const url = getFileUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const fileType = file?.file_type || getFileType(getFileUrl() || '');
  const isImage = fileType.startsWith('image/');
  const isPDF = fileType === 'application/pdf';

  // Debug logging for PDF detection
  console.log('FileViewerModal Debug:', {
    file: file,
    fileUrl: getFileUrl(),
    detectedFileType: fileType,
    isPDF: isPDF,
    filename: getFileName()
  });
  const isDocument = fileType.includes('msword') ||
                    fileType.includes('document') ||
                    fileType.includes('officedocument') ||
                    fileType.includes('opendocument') ||
                    fileType === 'application/rtf' ||
                    getFileName().toLowerCase().match(/\.(doc|docx|xls|xlsx|ppt|pptx|odt|ods|odp|rtf)$/);
  const isArchive = fileType.includes('zip') ||
                   fileType.includes('rar') ||
                   fileType.includes('7z') ||
                   fileType.includes('tar') ||
                   fileType.includes('gzip');
  const isAudio = fileType.startsWith('audio/');
  const isVideo = fileType.startsWith('video/');
  const isText = fileType.startsWith('text/') || fileType === 'text/csv';

  if (!isOpen || !file) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 animate-fade-in-down"
      aria-labelledby="file-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="fixed inset-0"
          onClick={handleBackdropClick}
        ></div>

        {/* Modal panel */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full h-[90vh] max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {isImage ? (
                  <PhotoIcon className="h-6 w-6 text-blue-600" />
                ) : (
                  <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                )}
              </div>
              <div>
                <h3
                  id="file-modal-title"
                  className="text-lg font-semibold text-gray-900 truncate max-w-md"
                  title={getFileName()}
                >
                  {getFileName()}
                </h3>
                <p className="text-sm text-gray-500">
                  {isImage ? 'Image File' :
                   isPDF ? 'PDF Document' :
                   isDocument ? 'Document File' :
                   isArchive ? 'Archive File' :
                   isAudio ? 'Audio File' :
                   isVideo ? 'Video File' :
                   isText ? 'Text File' :
                   'File Attachment'}
                </p>
              </div>
            </div>

            {/* Close button only */}
            <div className="flex items-center">
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-200 transition-colors duration-200"
                title="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content area */}
          <div className="flex-1 overflow-hidden bg-gray-100 h-full">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-500"></div>
                  <p className="text-gray-600">Loading preview...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Unable to preview this file</p>
                  <p className="text-sm text-gray-500">{error}</p>
                  <button
                    onClick={handleDownload}
                    className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download to view
                  </button>
                </div>
              </div>
            ) : isImage && fileUrl ? (
              <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                <img
                  src={fileUrl}
                  alt={getFileName()}
                  className="max-w-full max-h-full object-contain"
                  onError={() => setError('Failed to load image')}
                />
              </div>
            ) : isPDF && fileUrl ? (
              <div className="w-full h-full bg-white relative">
                {/* PDF Loading overlay */}
                {isPdfLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-20">
                    <div className="flex flex-col items-center space-y-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                      <p className="text-gray-600">Loading PDF...</p>
                    </div>
                  </div>
                )}

                <iframe
                  src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                  className="w-full h-full border-0"
                  title={getFileName()}
                  onLoad={() => {
                    setIsPdfLoading(false);
                  }}
                  onError={(e) => {
                    console.error('PDF iframe load error:', e);
                    setIsPdfLoading(false);
                    setError('Failed to load PDF - your browser may not support PDF viewing in iframes');
                  }}
                  style={{
                    minHeight: '600px'
                  }}
                />

              </div>
            ) : isAudio && fileUrl ? (
              <div className="flex items-center justify-center h-full bg-gray-50">
                <div className="text-center p-8">
                  <div className="mb-6">
                    <svg className="h-24 w-24 text-blue-500 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">{getFileName()}</h4>
                  <audio
                    controls
                    className="w-full max-w-md mx-auto mb-4"
                    preload="metadata"
                  >
                    <source src={fileUrl} />
                    Your browser does not support the audio element.
                  </audio>
                  <p className="text-sm text-gray-500">Audio File</p>
                </div>
              </div>
            ) : isVideo && fileUrl ? (
              <div className="w-full h-full bg-black flex items-center justify-center">
                <video
                  controls
                  className="max-w-full max-h-full"
                  preload="metadata"
                >
                  <source src={fileUrl} />
                  Your browser does not support the video element.
                </video>
              </div>
            ) : isText && fileUrl ? (
              <div className="w-full h-full bg-white p-6 overflow-auto">
                <iframe
                  src={fileUrl}
                  className="w-full h-full border-0"
                  title={getFileName()}
                  onError={() => setError('Failed to load text file')}
                />
              </div>
            ) : (
              // Default document preview
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  {isDocument ? (
                    <svg className="h-24 w-24 text-blue-500 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    </svg>
                  ) : isArchive ? (
                    <svg className="h-24 w-24 text-yellow-500 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14,17H12V15H14M14,13H12V11H14M14,9H12V7H14M17,3A1,1 0 0,1 18,4V6H16V4H8V6H6V4A1,1 0 0,1 7,3H17M11,6H13V8H11V6M9,8V10H11V8H9M13,10V8H15V10H13M15,12V10H17V12H15M17,14V12H19V14H17M19,16V14H21V16H19M9,10H7V12H9V10M7,12H5V14H7V12M5,14H3V16H5V14Z" />
                    </svg>
                  ) : (
                    <DocumentTextIcon className="h-24 w-24 text-gray-400 mx-auto mb-4" />
                  )}
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {getFileName()}
                  </h4>
                  <p className="text-gray-600 mb-2">
                    {isDocument ? 'Document File' :
                     isArchive ? 'Archive File' :
                     isAudio ? 'Audio File' :
                     isVideo ? 'Video File' :
                     isText ? 'Text File' :
                     'File'}
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    {isDocument ? 'Preview not available for this document type' :
                     isArchive ? 'Archive contents cannot be previewed' :
                     file?.description || 'Preview not available for this file type'}
                  </p>
                  <div className="space-x-3">
                    <button
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      {isDownloading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                          Download to View
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleOpenInNewTab}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                      Open in New Tab
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer with file info and action buttons */}
          <div className="bg-gray-50 border-t border-gray-200 rounded-b-lg">
            {/* File information */}
            {file?.submitted_by && (
              <div className="px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center space-x-4">
                    <span>
                      <strong>Submitted by:</strong> {file.submitted_by || 'Unknown'}
                    </span>
                    {file.file_size && (
                      <span>
                        <strong>Size:</strong> {(file.file_size / 1024).toFixed(2)} KB
                      </span>
                    )}
                    {fileType && (
                      <span>
                        <strong>Type:</strong> {fileType}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {file.created_at && new Date(file.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="px-4 py-3 flex items-center justify-end space-x-3">
              {/* Open in New Tab button */}
              <button
                type="button"
                onClick={handleOpenInNewTab}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                title="Open in New Tab"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                Open in New Tab
              </button>

              {/* Download button */}
              <button
                type="button"
                onClick={handleDownload}
                disabled={isDownloading}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-600 border border-transparent rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                title="Download"
              >
                {isDownloading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </>
                )}
              </button>

              {/* Close button */}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                title="Close"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FileViewerModal);
